import { Component, ChangeDetectorRef, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { CalendarEvent, CalendarEventTitleFormatter } from 'angular-calendar';
import { BaseComponent } from 'src/app/shared/components/base.component';
import { MessageService } from 'primeng/api';
import { takeUntil } from 'rxjs/operators';
import { TherapistAvailability } from 'src/app/shared/models/response/appointment/available-slots';
import { WebStorageService } from 'src/app/shared/services/web-storage.service';
import { CustomEventTitleFormatter } from 'src/app/shared/provider/custom-event-title-formatter.provider';
import moment from 'moment';
import { Title } from '@angular/platform-browser';
import { ManageAvailabilityService } from 'src/app/admin/servicies/manage-availability.service';

@Component({
  selector: 'app-my-availability-new',
  templateUrl: './my-availability-new.component.html',
  styleUrls: ['./my-availability-new.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: CalendarEventTitleFormatter,
      useClass: CustomEventTitleFormatter
    }
  ],
  standalone: false
})
export class MyAvailabilityNewComponent extends BaseComponent implements OnInit, OnDestroy {
  private unsubscriber = new Subject<boolean>();
  loadingCustom: boolean = false;
  viewDate: Date = new Date();
  events: CalendarEvent[] = [];
  private entherapistId: string;

  constructor(
    messageService: MessageService,
    private webStorageService: WebStorageService,
    protected cdr: ChangeDetectorRef,
    private therapistService: ManageAvailabilityService,
    private titleService: Title
  ) {
    super(messageService);
    this.titleService.setTitle('Appoint360');
  }

  ngOnInit() {
    const currentUserToken = this.webStorageService.getCurrentUserToken();
    const currentUserAuthToken = this.webStorageService.getCurrentUserAuthToken();
    if (currentUserToken && currentUserAuthToken.token) {
      this.entherapistId = currentUserToken.enuserId;
    }
    this.getTherapistAvailability();
  }

  dateChange() {
    this.getTherapistAvailability();
  }

  ngOnDestroy() {
    if (this.unsubscriber) {
      this.unsubscriber.next();
      this.unsubscriber.complete();
    }
  }

  private getTherapistAvailability() {
    this.loadingCustom = true;

    const selectedDate = `${this.viewDate.getFullYear()}-${this.viewDate.getMonth() + 1}-${this.viewDate.getDate()}`;

    this.therapistService
      .GetTherapistAvailabilityCalendar(this.entherapistId, selectedDate, false)
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(
        (result) => {
          this.processResult<TherapistAvailability[]>(result, () => {
            const events = result.responseModel.map((avail) => this.buildEvent(avail)).filter(Boolean); // Filters out null/undefined results from buildEvent (e.g., skipped or invalid availabilities)

            this.loadingCustom = false;
            this.events = events;
            this.cdr.detectChanges();
          });
        },
        (httpResponseError) => {
          this.loadingCustom = false;
          this.showErrorMessage(httpResponseError.message);
        }
      );
  }

  private buildEvent(avail: TherapistAvailability): any | null {
    const { custDateId, availId, startTime, endTime, title, isHoliday, isRoadShow, isRemote, isFacility, isOffice, isFullDay, isGlobalHoliday } = avail;

    // Skip if it's marked as both holiday and office
    if (isHoliday && isOffice && !isGlobalHoliday) {
      return null;
    }

    const formattedTitle = !isHoliday ? `${moment(startTime).format('hh:mm')} - ${title}` : title;

    return {
      id: `${custDateId},${availId}`,
      start: new Date(startTime),
      end: new Date(endTime),
      title: isGlobalHoliday ? title : formattedTitle,
      title1: title,
      isHoliday,
      isRoadShow,
      isRemote,
      isFacility,
      isOffice,
      isFullDay,
      isGlobalHoliday
    };
  }

  getEventClass(item: any): string {
    if (item.isHoliday) return 'view-by-available-slots_event off';
    if (item.isFacility) return 'view-by-available-slots_event facility';
    if (item.isRoadShow) return 'view-by-available-slots_event road-show';
    if (item.isRemote) return 'view-by-available-slots_event remote';
    if (item.isOffice) return 'view-by-available-slots_event office';
    return 'view-by-available-slots_event';
  }
}
